import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormikContext } from 'formik';
import React, { useState } from 'react';
import {
  AnyAssetApplication,
  FormikConnectionTest,
  FormikFormConnectionKey,
  FormikNewFormValues,
  Flex,
} from '@mss/mss-component-library';
import { useTranslation } from 'react-i18next';
import { Collapse } from 'antd';

type TestContainerProps = {
  horizontal?: boolean;
  last?: boolean;
  success: boolean | null;
  label: string;
  index?: string;
};

const resultColor: Record<string, string> = {
  true: 'text-success',
  false: 'text-danger',
  null: 'text-gray-300',
};

const TestResult = ({
  horizontal,
  success,
  last,
  label,
}: TestContainerProps) => {
  const horizontalNotLast = horizontal && !last;
  const notHorizontalNotLast = !horizontal && !last;
  const notHorizontalLast = !horizontal && last;

  return (
    <div
      className={`flex ${horizontal ? 'flex-row items-center' : 'flex-col'} ${notHorizontalLast ? 'w-full' : ''
        } ${horizontalNotLast ? 'basis-full' : ''}`}
    >
      <Flex h="start" v="center" className={horizontal ? 'basis-full' : ''}>
        <FontAwesomeIcon
          size="lg"
          className={resultColor[String(success)]}
          icon={faCheckCircle}
        />
        <span className="ml-2 whitespace-nowrap">{label}</span>
        {horizontalNotLast && <div className="w-full h-0.5 bg-gray-200 m-4" />}
      </Flex>
      {notHorizontalNotLast && (
        <div className="w-0.5 h-5 my-px mx-2 bg-gray-200" />
      )}
    </div>
  );
};

type Props = {
  compact?: boolean;
  resultLabels?: Record<keyof FormikConnectionTest['result']['status'], string>;
  resultKeys?: (keyof FormikConnectionTest['result']['status'])[];
  resultParentPath?: FormikFormConnectionKey;
  index?: number;
};

const defaultResultKeys: Required<Props>['resultKeys'] = [
  'port',
  'account',
  'query',
];

const defaultResultLabels: Required<Props>['resultLabels'] = {
  port: 'Port Validation',
  account: 'Account Validation',
  query: 'Query Validation',
};

function ConnectionResults<AssetType extends AnyAssetApplication>({
  compact = false,
  resultLabels = defaultResultLabels,
  resultKeys = defaultResultKeys,
  resultParentPath = 'test_direct',
  index = 0,
}: Props) {
  const { t } = useTranslation(['asset', 'common']);
  const { values } = useFormikContext<FormikNewFormValues<AssetType>>();

  const [openResult, setOpenResult] = useState<boolean>(false);

  const result = values.assets[index][resultParentPath].result;
  const hasRun = result.status[resultKeys[0]] != null;

  return (
    <div>
      <Flex column={!compact} h={'start'} v={compact ? 'center' : undefined}>
        {resultKeys.map((key, i, arr) => (
          <TestResult
            horizontal={compact}
            key={key}
            success={result.status[key]}
            label={resultLabels[key]}
            last={arr.length - 1 === i}
          />
        ))}
      </Flex>
      <Collapse
        bordered={false}
        ghost
        onChange={panels => setOpenResult(!!panels[0])}
      >
        <Collapse.Panel
          key="results"
          showArrow={false}
          className="cursor-default"
          header={
            <Flex h="end" v="center" className="w-full cursor-default">
              <button
                disabled={!hasRun}
                type="button"
                data-ta="showConnectionResults"
                className={`text-xs ${!hasRun ? 'text-muted opacity-60' : ' text-primary'
                  }`}
              >
                {openResult
                  ? t('Hide detailed result')
                  : t('Show detailed result')}
              </button>
            </Flex>
          }
        >
          <pre>
            {!result.error && typeof result.txt === 'string' && result.txt && <pre>{result.txt}</pre>}
            {result.error && <pre>{typeof result.error === 'string' ? result.error : JSON.stringify(result.error, null, 2)}</pre>}
            {!result.error && typeof result.message === 'string' && result.message && <pre>{result.message}</pre>}
            {!(typeof result.txt === 'string' && result.txt) &&
              !(result.error || typeof result.error === 'string') &&
              !(typeof result.message === 'string' && result.message) && (
                <pre>No result</pre>
              )}
          </pre>
        </Collapse.Panel>
      </Collapse>
    </div>
  );
}

export default ConnectionResults;
